import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { ProgressUpdateCard } from "@/features/progress-updates/progress-update-card";
import { CreateProgressUpdateDialog } from "@/features/progress-updates/create-progress-update-dialog";
import { useProgressUpdates } from "@/hooks/use-progress-updates";
import { useCurrentUser, isAdmin } from "@/hooks/use-current-user";
import type { KeyResult } from "@/types/entities";

interface ProgressUpdateListProps {
  keyResult: KeyResult;
}

export function ProgressUpdateList({ keyResult }: ProgressUpdateListProps) {
  const { currentUser } = useCurrentUser();
  const { data, isLoading } = useProgressUpdates(keyResult.id);

  const isOwner = currentUser?.id === keyResult.owner_id;
  const isAdminUser = isAdmin(currentUser?.role);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Progress Updates</CardTitle>
          {isOwner && <CreateProgressUpdateDialog krId={keyResult.id} />}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : !data?.data || data.data.length === 0 ? (
          <EmptyState
            title="No Progress Updates"
            description="Progress updates help track work and communicate status to supervisors."
          />
        ) : (
          <div className="space-y-3">
            {data.data.map((pu) => (
              <ProgressUpdateCard
                key={pu.id}
                progressUpdate={pu}
                krId={keyResult.id}
                isAuthor={currentUser?.id === pu.author_id}
                canSeeContent={isAdminUser || currentUser?.id === pu.author_id}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
